import React, { useState, useEffect } from 'react';
import BookingPayPalButton from './BookingPayPalButton';
import BookingStripeButton from './BookingStripeButton';
import BookingKlarnaButton from './BookingKlarnaButton';
import { API_BASE_URL, authFetch } from '../../config/api.config';
import './PaymentForm.css';

// T-021 (02.09.2026): Bankdaten kommen aus den SystemSettings (/systemsettings/public),
// der Verwendungszweck ist die Rechnungsnummer der offenen Rechnung (Bankabgleich matcht
// Rechnungsnummern mit hoher Sicherheit), Fallback Buchungsnummer. Nie mehr die interne ID.
export interface BankTransferDetails {
  invoiceNumber?: string;
  reference: string;
  accountHolder: string;
  bankName: string;
  iban: string;
  bic: string;
}

interface PaymentFormProps {
  bookingId: number;
  bookingNumber?: string;
  amount: number;
  onSuccess?: (paymentMethod: string, amount: number, details?: BankTransferDetails) => void;
  onCancel?: () => void;
}

interface EnabledPaymentMethods {
  stripeEnabled: boolean;
  paypalEnabled: boolean;
  klarnaEnabled: boolean;
  bankTransferEnabled: boolean;
}

interface BankDetails {
  accountHolder: string;
  bankName: string;
  iban: string;
  bic: string;
}

type PaymentMethodType = 'paypal' | 'stripe' | 'klarna' | 'bank_transfer' | null;

// Rechnungsstatus, bei denen die Rechnung noch offen ist (Backend-Enum InvoiceStatus)
const OPEN_INVOICE_STATUSES = ['Draft', 'Created', 'Sent', 'Viewed', 'PartiallyPaid', 'Overdue'];

const PaymentForm: React.FC<PaymentFormProps> = ({
  bookingId,
  bookingNumber,
  amount,
  onSuccess,
  onCancel
}) => {
  const [selectedMethod, setSelectedMethod] = useState<PaymentMethodType>(null);
  const [error, setError] = useState<string | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [enabledMethods, setEnabledMethods] = useState<EnabledPaymentMethods>({
    stripeEnabled: true,
    paypalEnabled: true,
    klarnaEnabled: true,
    bankTransferEnabled: true
  });
  const [bankDetails, setBankDetails] = useState<BankDetails | null>(null);
  const [bankDetailsError, setBankDetailsError] = useState(false);
  const [openInvoiceNumber, setOpenInvoiceNumber] = useState<string | null>(null);

  useEffect(() => {
    fetchEnabledPaymentMethods();
    fetchBankDetails();
  }, []);

  useEffect(() => {
    fetchOpenInvoiceNumber(bookingId);
  }, [bookingId]);

  const fetchEnabledPaymentMethods = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/systemsettings/payment-methods`);
      if (response.ok) {
        const data = await response.json();
        setEnabledMethods(data);
      }
    } catch (err) {
      console.error('Error fetching payment methods:', err);
      // Keep defaults on error
    }
  };

  // Echte Bankverbindung aus den Admin-Einstellungen (oeffentlicher Endpoint, kein Login noetig)
  const fetchBankDetails = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/systemsettings/public`);
      if (!response.ok) {
        setBankDetailsError(true);
        return;
      }
      const data = await response.json();
      if (!data.bankIban) {
        setBankDetailsError(true);
        return;
      }
      setBankDetails({
        accountHolder: data.companyName || '',
        bankName: data.bankName || '',
        iban: data.bankIban || '',
        bic: data.bankBic || ''
      });
    } catch (err) {
      console.error('Error fetching bank details:', err);
      setBankDetailsError(true);
    }
  };

  // Offene Rechnung zur Buchung (seit A3 wird sie bei Kunden-Buchungen sofort angelegt),
  // liefert die Rechnungsnummer als Verwendungszweck. Fehlt sie, gilt die Buchungsnummer.
  const fetchOpenInvoiceNumber = async (id: number) => {
    try {
      const response = await authFetch(`${API_BASE_URL}/payment/status/${id}`);
      if (!response.ok) return;
      const data = await response.json();
      const invoices: Array<{ invoiceNumber?: string; status?: string }> = Array.isArray(data?.invoices)
        ? data.invoices
        : [];
      // Backend liefert neueste zuerst
      const open = invoices.find(i => i.invoiceNumber && OPEN_INVOICE_STATUSES.includes(i.status || ''));
      if (open?.invoiceNumber) {
        setOpenInvoiceNumber(open.invoiceNumber);
      }
    } catch (err) {
      console.error('Error fetching open invoice:', err);
    }
  };

  const handleError = (errorMessage: string) => {
    setError(errorMessage);
    setTimeout(() => setError(null), 5000);
  };

  const handleBack = () => {
    setSelectedMethod(null);
    setError(null);
  };

  const formatCurrency = (cents: number) => {
    return new Intl.NumberFormat('de-DE', {
      style: 'currency',
      currency: 'EUR'
    }).format(cents / 100);
  };

  const paymentReference = openInvoiceNumber || bookingNumber || '';

  // Handle bank transfer - generates invoice via backend
  const handleBankTransferConfirm = async () => {
    setIsProcessing(true);
    setError(null);

    try {
      // Call dedicated endpoint to generate invoice for bank transfer (authFetch: 401-Refresh, T-009)
      const response = await authFetch(`${API_BASE_URL}/payment/generate-invoice`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          bookingId
        })
      });

      if (response.ok) {
        const result = await response.json();
        console.log('Bank transfer invoice created:', result);
        // Rechnungsnummer aus der Antwort ist die verbindliche Referenz fuer die Ueberweisung
        const invoiceNumber: string | undefined = result?.invoiceNumber || result?.InvoiceNumber || openInvoiceNumber || undefined;
        onSuccess?.('bank_transfer', amount, {
          invoiceNumber,
          reference: invoiceNumber || paymentReference,
          accountHolder: bankDetails?.accountHolder || '',
          bankName: bankDetails?.bankName || '',
          iban: bankDetails?.iban || '',
          bic: bankDetails?.bic || ''
        });
      } else if (response.status === 401) {
        handleError('Ihre Sitzung ist abgelaufen. Bitte melden Sie sich erneut an.');
      } else {
        const errorText = await response.text();
        console.error('Bank transfer init error:', errorText);
        handleError('Rechnung konnte nicht erstellt werden. Bitte versuchen Sie es erneut.');
      }
    } catch (err: any) {
      console.error('Bank transfer error:', err);
      handleError(err.message || 'Fehler bei der Rechnungserstellung');
    } finally {
      setIsProcessing(false);
    }
  };

  // Show method selection
  if (!selectedMethod) {
    return (
      <div className="payment-form">
        {error && (
          <div className="payment-error-banner">
            <span>{error}</span>
          </div>
        )}

        <div className="payment-header">
          <h2>Zahlungsmethode wählen</h2>
          <p className="payment-amount">
            Zu zahlender Betrag: <strong>{formatCurrency(amount)}</strong>
          </p>
        </div>

        <div className="payment-method-cards">
          {/* PayPal */}
          {enabledMethods.paypalEnabled && (
            <div
              className="payment-method-card"
              onClick={() => setSelectedMethod('paypal')}
            >
              <div className="method-icon">
                <span style={{ color: '#003087', fontWeight: 'bold' }}>Pay</span>
                <span style={{ color: '#009cde', fontWeight: 'bold' }}>Pal</span>
              </div>
              <h4>PayPal</h4>
              <p>Schnell und sicher</p>
              <div className="method-badges">
                <span className="badge">Käuferschutz</span>
              </div>
            </div>
          )}

          {/* Kreditkarte (Stripe) */}
          {enabledMethods.stripeEnabled && (
            <div
              className="payment-method-card"
              onClick={() => setSelectedMethod('stripe')}
            >
              <div className="method-icon">💳</div>
              <h4>Kreditkarte</h4>
              <p>Visa, Mastercard, Amex</p>
              <div className="method-badges">
                <span className="badge">3D Secure</span>
              </div>
            </div>
          )}

          {/* Sofortüberweisung (Klarna) */}
          {enabledMethods.klarnaEnabled && (
            <div
              className="payment-method-card"
              onClick={() => setSelectedMethod('klarna')}
            >
              <div className="method-icon">🏦</div>
              <h4>Sofortüberweisung</h4>
              <p>Direkt von Ihrem Konto</p>
              <div className="method-badges">
                <span className="badge">Sofort</span>
              </div>
            </div>
          )}

          {/* Banküberweisung */}
          {enabledMethods.bankTransferEnabled && (
            <div
              className="payment-method-card"
              onClick={() => setSelectedMethod('bank_transfer')}
            >
              <div className="method-icon">📋</div>
              <h4>Überweisung</h4>
              <p>Klassische SEPA-Überweisung</p>
              <div className="method-badges">
                <span className="badge">3-5 Tage</span>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }

  // Show selected payment method
  return (
    <div className="payment-form">
      {error && (
        <div className="payment-error-banner">
          <span>{error}</span>
        </div>
      )}

      <button className="back-button" onClick={handleBack}>
        ← Andere Zahlungsmethode wählen
      </button>

      {selectedMethod === 'paypal' && (
        <div className="payment-method-content">
          <h3>Mit PayPal bezahlen</h3>
          <BookingPayPalButton
            bookingId={bookingId}
            amount={amount}
            onError={handleError}
            onCancel={handleBack}
          />
        </div>
      )}

      {selectedMethod === 'stripe' && (
        <div className="payment-method-content">
          <h3>Mit Kreditkarte bezahlen</h3>
          <BookingStripeButton
            bookingId={bookingId}
            amount={amount}
            onError={handleError}
            onCancel={handleBack}
          />
        </div>
      )}

      {selectedMethod === 'klarna' && (
        <div className="payment-method-content">
          <h3>Mit Sofortüberweisung bezahlen</h3>
          <BookingKlarnaButton
            bookingId={bookingId}
            amount={amount}
            onError={handleError}
            onCancel={handleBack}
          />
        </div>
      )}

      {selectedMethod === 'bank_transfer' && (
        <div className="payment-method-content bank-transfer">
          <h3>🏦 Banküberweisung</h3>

          <div className="transfer-info-box">
            <h4>Überweisungsdaten</h4>
            {bankDetailsError && (
              <p className="transfer-details-error">
                Die Bankverbindung konnte nicht geladen werden. Sie erhalten die Überweisungsdaten
                mit Ihrer Rechnung per E-Mail.
              </p>
            )}
            {!bankDetailsError && !bankDetails && (
              <p className="transfer-details-loading">Bankverbindung wird geladen...</p>
            )}
            {bankDetails && (
              <div className="transfer-details">
                <div className="detail-row">
                  <span className="label">Empfänger:</span>
                  <span className="value">{bankDetails.accountHolder}</span>
                </div>
                {bankDetails.bankName && (
                  <div className="detail-row">
                    <span className="label">Bank:</span>
                    <span className="value">{bankDetails.bankName}</span>
                  </div>
                )}
                <div className="detail-row">
                  <span className="label">IBAN:</span>
                  <span className="value">{bankDetails.iban}</span>
                </div>
                {bankDetails.bic && (
                  <div className="detail-row">
                    <span className="label">BIC:</span>
                    <span className="value">{bankDetails.bic}</span>
                  </div>
                )}
                <div className="detail-row">
                  <span className="label">Verwendungszweck:</span>
                  <span className="value">{paymentReference || 'siehe Rechnung'}</span>
                </div>
                <div className="detail-row highlight">
                  <span className="label">Betrag:</span>
                  <span className="value amount">{formatCurrency(amount)}</span>
                </div>
              </div>
            )}
          </div>

          <div className="transfer-notice">
            <p><strong>Wichtig:</strong> Bitte überweisen Sie innerhalb von 7 Tagen.</p>
            <p>Sie erhalten eine E-Mail mit Ihrer Rechnung und den Überweisungsdaten.</p>
          </div>

          <button
            className="confirm-button"
            onClick={handleBankTransferConfirm}
            disabled={isProcessing || (!bankDetails && !bankDetailsError)}
          >
            {isProcessing ? 'Wird verarbeitet...' : 'Buchung bestätigen'}
          </button>
        </div>
      )}
    </div>
  );
};

export default PaymentForm;
